import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { flip, offset, shift, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import {
  ParticipantContextIfNeeded,
  TrackRefContextIfNeeded,
  VideoTrack,
  useIsMuted,
  useMultibandTrackVolume,
  useSpeakingParticipants,
  useTracks,
} from '@livekit/components-react';
import { patch } from '@rails/request.js';
import { useLiveQuery } from 'dexie-react-hooks';
import { ParticipantKind, Track } from 'livekit-client';
import { ChevronDown, Mic, MicOff, Monitor, MonitorOff, Play, Video, VideoOff } from 'lucide-react';

import { apiRoute } from '../../lib/api';
import { CHAT_COLUMN_SELECTOR, CHAT_COLUMN_TOP_INSET } from '../../lib/chatColumnAnchor';
import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { logError } from '../../lib/logger';
import { useSingleChannelLatestConferenceTranscript } from '../../lib/useSingleChannelLatestConferenceTranscript';
import { cn } from '../../lib/utils';

import { useConference } from '../../contexts/ConferenceContext';
import { Flexicon } from '../shared/Flexicon';
import { Tooltip } from '../shared/Tooltip';
import { ConferenceDeviceMenu } from './ConferenceDeviceMenu';
import { useDraggableWindowPosition } from './useDraggableWindowPosition';
import { useMediaDevicePreferences } from './useMediaDevicePreferences';

// Resting bar heights: silence shows a static waveform silhouette rather than three equal
// nubs, which read as an ellipsis.
const BAR_RESTING_LEVELS = [0.35, 0.7, 0.35];

// useTracks memoises on the identity of the array it is handed, so these have to be module
// constants. Inline literals would rebuild the placeholder track list — a walk over every
// participant's publications — on every render, and dragging the window renders per pointer event.
const MICROPHONE_SOURCES = [Track.Source.Microphone];
const CAMERA_SOURCES = [{ source: Track.Source.Camera, withPlaceholder: true }];
const SCREEN_SHARE_SOURCES = [{ source: Track.Source.ScreenShare }];

function ListeningEqualizer() {
  const micTracks = useTracks(MICROPHONE_SOURCES);
  const speakingParticipants = useSpeakingParticipants();
  const speakingIdentity = speakingParticipants?.[0]?.identity;

  // Hold the last speaker's track through silences: swapping it tears down and rebuilds
  // the Web Audio analyser, and doing that on every pause in speech thrashes.
  const lastSpeakingTrackRef = useRef(undefined);
  const speakingTrack = useMemo(() => {
    const match = speakingIdentity ? micTracks.find((track) => track.participant?.identity === speakingIdentity) : undefined;
    if (match) lastSpeakingTrackRef.current = match;
    return match ?? lastSpeakingTrackRef.current;
  }, [micTracks, speakingIdentity]);

  const bands = useMultibandTrackVolume(speakingTrack, { bands: 3, updateInterval: 80 });

  return (
    <span aria-hidden className="flex h-3 shrink-0 items-center gap-[2px]">
      {[0, 1, 2].map((bar) => {
        const level = Math.min(1, (bands?.[bar] ?? 0) * 3.5);
        return (
          <span
            key={bar}
            className="w-[2px] rounded-full bg-white transition-[height] duration-75"
            style={{ height: `${Math.round(Math.max(BAR_RESTING_LEVELS[bar], level) * 100)}%` }}
          />
        );
      })}
    </span>
  );
}

// Each corner drives the horizontal edge it belongs to, but wears the diagonal cursor a
// window corner is expected to have.
const CORNER_HANDLES = [
  { edge: 'left', position: 'top-0 left-0', cursor: 'cursor-nwse-resize' },
  { edge: 'right', position: 'top-0 right-0', cursor: 'cursor-nesw-resize' },
  { edge: 'left', position: 'bottom-0 left-0', cursor: 'cursor-nesw-resize' },
  { edge: 'right', position: 'bottom-0 right-0', cursor: 'cursor-nwse-resize' },
];

// The media controls sit bare, with spacing rather than a container doing the grouping —
// End keeps the only fill in the row, and the transcription chip the only other one. The
// extra 4px on the right buys air against the transcription chip, so the bare glyphs read as
// their own set rather than crowding the first thing in the row that has an edge.
// gap-1, not gap-3: each set now carries px-2 of its own, so 4px between them lands the
// glyphs at the same spacing they had when the sets were bare and the gap did all the work.
const controlClusterClassName = 'mr-1 flex shrink-0 items-center gap-1';

// A toggle and its chevron share one hover container, so hovering either lights the pair and
// the pairing is visible rather than implied by spacing. The set is the hover target; the
// buttons inside it stay bare, which is what keeps two containers from colliding.
const controlSetClassName = (active) =>
  cn('flex h-7 shrink-0 items-center gap-0.5 rounded-full px-2', active ? 'hover:bg-red-500/15' : 'hover:bg-white/15');

// A true 2px stroke closed up the counters at this size and the icons read as smudges. At
// 1px the glyphs stay open and land on whole device pixels at 2x, which is what lets them
// sit this small inside the pill without going soft.
const CONTROL_ICON_PROPS = { strokeWidth: 1, absoluteStrokeWidth: true };

// Bare inside its set — the set draws the hover, this only carries colour.
//
// Sizes are set with lucide's `size` prop, never a CSS class: `absoluteStrokeWidth` divides
// by that prop, so a class-sized icon keeps lucide's default of 24 and renders its stroke
// scaled instead. Spread CONTROL_ICON_PROPS on every icon in here so the weight can't drift
// between them — the size then changes the glyph's footprint and nothing else.
const controlIconClassName = (active) =>
  cn(
    'flex shrink-0 cursor-pointer items-center justify-center [&_*]:transition-none',
    // A step lighter than End's fill: the same alarm colour family, but a stroked icon at
    // red-600 shouts louder than a filled button does at the same value.
    active ? 'text-red-500' : 'text-white',
  );

// A toggle with its device picker on a chevron beside it. Permanent rather than
// hover-revealed — the title bar has the room, and hiding it caused more trouble than
// the space was worth.
function ControlWithDeviceMenu({ label, menuLabel, sections, isMuted, onToggle, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: menuOpen,
    onOpenChange: setMenuOpen,
    placement: 'bottom-start',
    middleware: [offset({ mainAxis: 8 }), flip({ fallbackPlacements: ['bottom-start', 'top-start'] }), shift({ padding: 8 })],
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([useClick(context), useDismiss(context)]);

  return (
    <div className={controlSetClassName(isMuted)}>
      <Tooltip label={label} placement="bottom">
        <button onClick={onToggle} aria-label={label} className={controlIconClassName(isMuted)}>
          {children}
        </button>
      </Tooltip>
      <Tooltip label={menuLabel} placement="bottom">
        <button
          ref={refs.setReference}
          aria-label={menuLabel}
          // Dimmer at rest so it reads as secondary to the toggle it belongs to.
          className="flex shrink-0 cursor-pointer items-center justify-center text-white/60 hover:text-white [&_*]:transition-none"
          {...getReferenceProps()}
        >
          <ChevronDown size={12} {...CONTROL_ICON_PROPS} />
        </button>
      </Tooltip>
      {menuOpen ? (
        <ConferenceDeviceMenu sections={sections} refs={refs} floatingStyles={floatingStyles} getFloatingProps={getFloatingProps} />
      ) : null}
    </div>
  );
}

// A share takes the window's main plane rather than opening anything of its own. Aspect
// comes from the track, so a portrait window or an ultrawide desktop keeps its shape, and
// object-contain letterboxes rather than crops — losing the edge of someone's document is
// worse than a black band.
//
// The sharer sees the same thing everyone else does. Sharing a whole display that has this
// window on it gives the usual hall of mirrors, exactly as it does in Meet or Zoom; window
// and tab shares, which is what people mostly pick, never hit it.
function ScreenShareTile({ track }) {
  const { profileId } = useConference();
  const isMine = profileId === track.participant?.identity;
  const trackReady = track.publication?.subscribed || isMine;

  return (
    // No declared aspect, no height cap, no fill: the video sizes itself from its own frame,
    // so there is never a letterbox to colour in and never a question of centring it. A
    // portrait share does make the window tall — narrowing it is the answer, and that beats
    // permanent bars on every landscape share to guard against a rare one.
    <div className={cn('relative w-full overflow-hidden', !trackReady && 'aspect-video')}>
      <TrackRefContextIfNeeded trackRef={track}>
        <ParticipantContextIfNeeded participant={track.participant}>
          {trackReady ? <VideoTrack trackRef={track} className="block w-full" /> : null}
        </ParticipantContextIfNeeded>
      </TrackRefContextIfNeeded>
    </div>
  );
}

function ParticipantTile({ track, className }) {
  const { workspaceId, profileId } = useConference();
  const cameraIsMuted = useIsMuted(Track.Source.Camera, { participant: track.participant });
  const microphoneIsMuted = useIsMuted(Track.Source.Microphone, { participant: track.participant });

  const participantProfile = useLiveQuery(
    () => (track.participant?.identity ? db.profiles.get([workspaceId, track.participant.identity]) : null),
    [track.participant?.identity, workspaceId],
    DEXIE_IS_LOADING,
  );

  const isMine = profileId === track.participant?.identity;
  const trackReady = (track.publication?.subscribed || isMine) && participantProfile !== DEXIE_IS_LOADING;

  return (
    <div className={cn('relative aspect-video w-full overflow-hidden bg-gray-950', className)}>
      <TrackRefContextIfNeeded trackRef={track}>
        <ParticipantContextIfNeeded participant={track.participant}>
          {trackReady && (
            <>
              {!cameraIsMuted && (
                <VideoTrack
                  trackRef={track}
                  className={cn('absolute inset-0 size-full bg-center object-cover', isMine && 'scale-x-[-1] will-change-transform')}
                />
              )}

              {cameraIsMuted && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Flexicon resource={participantProfile} size="size-10" />
                </div>
              )}

              {microphoneIsMuted && (
                <div className="absolute top-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-black/50 text-white">
                  <MicOff size={12} strokeWidth={1} absoluteStrokeWidth />
                </div>
              )}
            </>
          )}
        </ParticipantContextIfNeeded>
      </TrackRefContextIfNeeded>
    </div>
  );
}

// One draggable window holding the call: a title bar of controls over a grid of faces.
// Fixed to the viewport rather than living in the layout, so it floats across every chat
// and survives navigation along with the room itself.
export function ConferenceWindow() {
  const {
    connectedChannel,
    microphoneIsMuted,
    cameraIsMuted,
    leaveConference,
    toggleCamera,
    toggleMicrophone,
    toggleScreenShare,
    profileId,
    workspaceId,
  } = useConference();

  // Calls are always started from the parent chat, so the window wears the chat's own
  // identity — a breakout's "#" avatar would name a room the call never belongs to.
  const conferenceWorkspace = useLiveQuery(() => (workspaceId ? db.workspaces.get(workspaceId) : null), [workspaceId], null);

  const devices = useMediaDevicePreferences();
  const cameraTracks = useTracks(CAMERA_SOURCES);
  const participantTracks = useMemo(
    () => cameraTracks.filter((track) => track.participant?.kind !== ParticipantKind.AGENT),
    [cameraTracks],
  );

  // Only one share runs at a time, so the first track is the share.
  const screenShareTracks = useTracks(SCREEN_SHARE_SOURCES);
  const screenShareTrack = screenShareTracks?.[0] ?? null;
  const shareIsMine = useMemo(
    () => screenShareTracks.some((track) => track.participant?.identity === profileId),
    [screenShareTracks, profileId],
  );

  const transcript = useSingleChannelLatestConferenceTranscript(connectedChannel?.id);
  const transcriptId = transcript !== DEXIE_IS_LOADING ? transcript?.id : null;
  const serverTranscriptionPaused = transcript !== DEXIE_IS_LOADING ? (transcript?.data?.transcription_paused ?? false) : false;

  // The write round-trips through the server and into Dexie before the live query sees it,
  // which is a visible beat on a control this small — long enough that the button reads as
  // broken. Show the new state on click and let the server's own value take back over once
  // it agrees, or snap back if the write failed.
  const [pendingTranscriptionPaused, setPendingTranscriptionPaused] = useState(null);
  const transcriptionPaused = pendingTranscriptionPaused ?? serverTranscriptionPaused;

  useEffect(() => {
    if (pendingTranscriptionPaused !== null && serverTranscriptionPaused === pendingTranscriptionPaused) {
      setPendingTranscriptionPaused(null);
    }
  }, [pendingTranscriptionPaused, serverTranscriptionPaused]);

  const handleToggleTranscription = useCallback(async () => {
    if (!transcriptId || !connectedChannel?.id) return;
    const next = !transcriptionPaused;
    setPendingTranscriptionPaused(next);
    try {
      await patch(apiRoute(`/v1/channels/${connectedChannel.id}/messages/${transcriptId}/update_transcription`), {
        body: { message: { transcription_paused: next } },
      });
    } catch (error) {
      logError('ConferenceWindow', 'Failed to toggle transcription', { error });
      setPendingTranscriptionPaused(null);
    }
  }, [connectedChannel?.id, transcriptId, transcriptionPaused]);

  const { nodeRef, position, width, dragHandleProps, resizeHandleProps } = useDraggableWindowPosition({
    callKey: connectedChannel?.id,
    // Sit on the same line as the channels card, which insets from the same box. Matching it
    // means measuring that exact box — the outer panel starts higher and put us ~20px low.
    topAnchor: CHAT_COLUMN_SELECTOR,
    topOffset: CHAT_COLUMN_TOP_INSET,
    // The hook narrows this to whatever the viewport can hold, so the ceiling only has to
    // stop the window becoming silly on an ultrawide display.
    width: { initial: 512, min: 384, max: 1600 },
  });

  return (
    <div
      ref={nodeRef}
      style={position ? { left: position.left, top: position.top, width } : { width }}
      className={cn(
        'fixed z-(--stack-conference-bubble-wrapper) hidden overflow-hidden rounded-2xl md:block',
        // The channels card's dark recipe, held in both modes rather than swapped with the
        // background. Video is dark whatever the workspace is set to, so a surface that
        // flipped to light would only ever frame the faces in the wrong key.
        'bg-image-glass-card',
        !position && 'invisible',
      )}
    >
      {/* Title bar doubles as the drag handle. Label first, then the controls, all packed
          left — wider gaps between control groups than inside one, since with no pill drawing
          the boundary spacing is what tells you which chevron belongs to which toggle. */}
      {/* Default arrow at rest, grabbing only once you actually press: a permanent grab hand
          advertises dragging as the bar's purpose, when mostly you're here for the controls. */}
      <div {...dragHandleProps} className="flex cursor-default items-center gap-3 px-3 py-2.5 active:cursor-grabbing">
        {/* Avatar and name read as one label, not a control — no fill, no hover, nothing
            that suggests pressing it does something. The name is capped at 120px so a long
            chat title truncates instead of walking the End button off the bar. */}
        {conferenceWorkspace ? (
          <div className="flex min-w-0 shrink-0 items-center gap-1.5">
            <Flexicon resource={conferenceWorkspace} size="size-5" className="shrink-0" />
            <span className="max-w-[120px] truncate text-xs leading-5 font-medium text-white">{conferenceWorkspace.title}</span>
          </div>
        ) : null}

        <div className="ml-auto flex shrink-0 items-center gap-3">
          {/* No container: spacing groups the media controls, and the gap out to the
              transcription chip is what separates "my inputs" from the call's own state. */}
          <div className={controlClusterClassName}>
            <ControlWithDeviceMenu
              label={cameraIsMuted ? 'Turn on camera' : 'Turn off camera'}
              menuLabel="Select camera"
              sections={[devices.camera]}
              isMuted={cameraIsMuted}
              onToggle={toggleCamera}
            >
              {/* Lucide's camera is a wide, mostly-empty rectangle, so at a matched box it
                  reads smaller than the mic and monitor. Two extra pixels even it out. */}
              {cameraIsMuted ? <VideoOff size={18} {...CONTROL_ICON_PROPS} /> : <Video size={18} {...CONTROL_ICON_PROPS} />}
            </ControlWithDeviceMenu>

            <ControlWithDeviceMenu
              label={microphoneIsMuted ? 'Turn on microphone' : 'Turn off microphone'}
              menuLabel="Select microphone and speaker"
              sections={[devices.microphone, devices.speaker]}
              isMuted={microphoneIsMuted}
              onToggle={toggleMicrophone}
            >
              {microphoneIsMuted ? <MicOff size={16} {...CONTROL_ICON_PROPS} /> : <Mic size={16} {...CONTROL_ICON_PROPS} />}
            </ControlWithDeviceMenu>

            {/* No device menu, so it's a set of one — same container, same hover. */}
            <div className={controlSetClassName(shareIsMine)}>
              <Tooltip label={shareIsMine ? 'Stop sharing' : 'Share screen'} placement="bottom">
                <button
                  onClick={toggleScreenShare}
                  aria-label={shareIsMine ? 'Stop sharing' : 'Share screen'}
                  className={controlIconClassName(shareIsMine)}
                >
                  {shareIsMine ? <MonitorOff size={16} {...CONTROL_ICON_PROPS} /> : <Monitor size={16} {...CONTROL_ICON_PROPS} />}
                </button>
              </Tooltip>
            </div>
          </div>

          {transcriptId ? (
            <Tooltip label={transcriptionPaused ? 'Resume transcription' : 'Pause transcription'} placement="bottom">
              <button
                onClick={handleToggleTranscription}
                aria-label={transcriptionPaused ? 'Resume transcription' : 'Pause transcription'}
                // Paused recedes rather than alarms: the chip loses most of its fill and the
                // glyph dims, so a stopped transcript reads as switched off. Red is spoken
                // for by the two mute states and End — a third would dilute all of them.
                // Fixed box rather than padding around the content: the equalizer is 10px
                // wide and the play glyph 12px, so sizing to the content shunted End sideways
                // every time transcription started or stopped.
                className={cn(
                  'flex h-6 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-none [&_*]:transition-none',
                  transcriptionPaused ? 'bg-white/8 hover:bg-white/15' : 'bg-white/20 hover:bg-white/30',
                )}
              >
                {transcriptionPaused ? (
                  <Play size={12} strokeWidth={1} absoluteStrokeWidth fill="currentColor" className="text-white/50" />
                ) : (
                  <ListeningEqualizer />
                )}
              </button>
            </Tooltip>
          ) : null}

          <button
            onClick={leaveConference}
            className="flex h-6 shrink-0 cursor-pointer items-center rounded-full bg-red-600 px-3 text-xs font-medium text-white hover:bg-red-700"
          >
            End
          </button>
        </div>
      </div>

      {screenShareTrack ? <ScreenShareTile track={screenShareTrack} /> : null}

      {/* Without a share: one face fills the width, beyond that they pair up and wrap.
          With one: the faces condense to quarter-width and centre themselves under it, so a
          partial last row sits evenly instead of leaving a hole at one end. */}
      <div
        className={cn(
          screenShareTrack
            ? 'flex flex-wrap justify-center'
            : cn(
                'grid',
                participantTracks.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
                // An odd count leaves the last tile alone on its row. It keeps a tile's
                // normal half-width and centers instead of stretching, so the leftover space
                // sits evenly on both sides rather than reading as a hole in the corner.
                participantTracks.length > 1 &&
                  participantTracks.length % 2 === 1 &&
                  '[&>*:last-child]:col-span-2 [&>*:last-child]:mx-auto [&>*:last-child]:w-1/2',
              ),
        )}
      >
        {participantTracks.map((track) => (
          <ParticipantTile
            key={track.participant.identity + track.source}
            track={track}
            className={screenShareTrack ? 'w-1/6' : undefined}
          />
        ))}
      </div>

      {/* Side edges and all four corners resize, the way any window does — no visible grip,
          just the cursor. Width is the only thing dragged; the face grid reflows to it and
          sets its own height, so the aspect ratio can never be stretched. Corners come after
          the edges so they win the overlap and show the diagonal cursor. */}
      <div
        {...resizeHandleProps('left')}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize call window"
        className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize"
      />
      <div
        {...resizeHandleProps('right')}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize call window"
        className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize"
      />
      {CORNER_HANDLES.map(({ edge, position: cornerPosition, cursor }) => (
        <div key={cornerPosition} {...resizeHandleProps(edge)} aria-hidden className={cn('absolute size-3.5', cornerPosition, cursor)} />
      ))}
    </div>
  );
}
